/**
 * 결제 링크 받는 쪽 — `/pay?t=` (261020-pay-link). 공개 화면이고 **휴대폰이 기준**이다.
 * 운영자가 카톡 등으로 보낸 주소를 열면 로그인 없이 카드를 넣고 바로 결제한다. 한 화면으로 끝난다:
 * 무엇을 얼마에 → 카드 → 확인 사항·동의 → 결제(아래 고정 버튼).
 *
 * ★ 토큰은 주소창에서 곧바로 지우고 그 탭의 sessionStorage 에만 둔다 — 인앱 브라우저가 앱을 오가며 다시 읽어도 이어진다.
 *   결제가 끝나면 지운다. 무효 토큰은 한 문장으로 답한다(서버도 사유를 가르지 않는다)
 * ★ 칸의 글자 크기는 16px 이상이다 — 그보다 작으면 iOS 사파리가 입력할 때 화면을 확대한다
 * ★ 금액·날짜는 서버 값만 그린다(화면이 VAT 를 계산하지 않는다)
 */
import { billing } from '@geo-admin/shared/contract/api/index.ts';
import { SUBSCRIPTION_TERMS_VERSION, type PayLinkView } from '@geo-admin/shared/contract/api/billing.ts';
import { useEffect, useRef, useState } from 'react';
import { BLANK_CARD, CardFields, cardInputOf } from '../components/CardFields.tsx';
import { AlertIcon, CheckIcon } from '../components/icons.tsx';
import { LogoMark } from '../components/LogoMark.tsx';
import { SupportContact } from '../components/SupportContact.tsx';
import { TermsSummary } from '../components/TermsSummary.tsx';
import { Button } from '../components/ui/button.tsx';
import { FieldError } from '../components/ui/form.tsx';
import { ApiError, call } from '../lib/call.ts';
import { SHELL } from '../lib/branding.ts';
import { clearUrlToken, useUrlToken } from '../lib/urlToken.ts';

const TOKEN_KEY = 'geo-pay-link';
const LINK_FAILED = '결제 링크가 만료됐거나 이미 사용됐습니다. 담당자에게 새 링크를 요청해 주세요.';

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const dot = (d: string) => d.replaceAll('-', '.');

type Done = Awaited<ReturnType<typeof call<typeof billing.completePayLink>>>;
type State =
  | { kind: 'loading' }
  | { kind: 'form'; view: PayLinkView }
  | { kind: 'done'; result: Done }
  | { kind: 'checking' }
  | { kind: 'fail'; message: string };

/** 서버가 결과를 모르는 결제(무응답)·확인 대기 — 다시 결제하게 두면 이중 결제가 될 수 있다 */
const CHECKING_CODES = new Set(['payment-unknown', 'payment-checking', 'payment-unconfirmed']);
/** 링크 자체가 더는 못 쓴다 */
const DEAD_CODES = new Set(['pay-link-invalid', 'pay-link-attempts', 'not-offered', 'not-live']);

export function PayLinkPage() {
  const token = useUrlToken(TOKEN_KEY);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setState({ kind: 'fail', message: LINK_FAILED });
      return;
    }
    void call(billing.inspectPayLink, { body: { token } })
      .then((view) => setState(view.blocked ? { kind: 'checking' } : { kind: 'form', view }))
      .catch((e: Error) =>
        setState({
          kind: 'fail',
          message: e instanceof ApiError && e.code === 'rate-limited' ? e.message : LINK_FAILED,
        }),
      );
  }, [token]);

  return (
    <div className="min-h-[100dvh] bg-slate-50 font-suit">
      <header className="flex h-14 items-center justify-center border-slate-200 border-b bg-white">
        <LogoMark tone="onLight" size="sm" />
      </header>
      <main className="mx-auto w-full max-w-md px-4 pt-5 pb-10">
        {state.kind === 'loading' ? <p className="py-16 text-center text-slate-500 text-sm">불러오는 중…</p> : null}
        {state.kind === 'form' ? (
          <PayForm
            token={token}
            view={state.view}
            onDone={(result) => {
              clearUrlToken(TOKEN_KEY);
              setState({ kind: 'done', result });
            }}
            onChecking={() => {
              clearUrlToken(TOKEN_KEY);
              setState({ kind: 'checking' });
            }}
            onDead={(message) => {
              clearUrlToken(TOKEN_KEY);
              setState({ kind: 'fail', message });
            }}
          />
        ) : null}
        {state.kind === 'done' ? <Paid result={state.result} /> : null}
        {state.kind === 'checking' ? (
          <Result tone="amber" title="결제 결과를 확인하고 있습니다">
            결제 결과를 바로 확인하지 못했습니다. 다시 결제하지 마시고 잠시 기다려 주세요 — 담당자가 확인한 뒤
            안내드립니다.
          </Result>
        ) : null}
        {state.kind === 'fail' ? (
          <Result tone="red" title="결제 링크를 열 수 없습니다">
            {state.message}
          </Result>
        ) : null}
      </main>
    </div>
  );
}

function PayForm({
  token,
  view,
  onDone,
  onChecking,
  onDead,
}: {
  token: string;
  view: PayLinkView;
  onDone: (r: Done) => void;
  onChecking: () => void;
  onDead: (message: string) => void;
}) {
  const [card, setCard] = useState(BLANK_CARD);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [left, setLeft] = useState(view.attemptsLeft);
  const free = view.price.amountKrw === 0;
  const recover = view.kind === 'recover';
  const cta = busy
    ? '결제하는 중…'
    : free
      ? '카드 등록하고 시작하기'
      : `${won(view.price.amountKrw)} ${recover ? '결제하기' : '결제하고 시작하기'}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !consent || left <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await call(billing.completePayLink, {
        body: { token, card: cardInputOf(card), consent: true, termsVersion: SUBSCRIPTION_TERMS_VERSION },
      });
      setCard(BLANK_CARD);
      onDone(r);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      if (CHECKING_CODES.has(code)) return onChecking();
      if (DEAD_CODES.has(code)) return onDead((err as Error).message || LINK_FAILED);
      // 카드 거절·결제 거절 — 그 카드 입력은 한 번으로 세어졌다. 비밀번호만 지우고 다시 받는다
      if (code !== 'rate-limited' && code !== 'validation') setLeft((n) => Math.max(0, n - 1));
      setCard((c) => ({ ...c, password: '' }));
      // 형식 오류(validation)는 서버 원문이 칸 이름을 담고 있어 사람 문장으로 바꾼다 — 카드 입력으로 세지 않는다
      setError(
        code === 'validation'
          ? '카드 정보를 다시 확인해 주세요.'
          : (err as Error).message || '결제하지 못했습니다. 카드 정보를 확인해 주세요.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="grid gap-4 pb-24" autoComplete="off" onSubmit={submit} id="pay-form">
      {view.testMode ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 text-sm">
          테스트 결제 환경입니다 — 실제 카드 대금이 청구되지 않습니다.
        </div>
      ) : null}

      {/* 무엇을 얼마에 */}
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <p className="text-slate-500 text-sm">{view.brandName}</p>
        <h1 className="mt-0.5 break-keep font-bold text-lg text-slate-900 leading-snug">{view.subscriptionName}</h1>
        <div className="mt-4 flex items-end justify-between gap-3">
          <span className="text-slate-500 text-sm">{recover ? '밀린 이용료' : '월 이용료'}</span>
          <span className="font-bold text-2xl text-slate-900 tabular-nums">
            {free ? '무료' : won(view.price.amountKrw)}
          </span>
        </div>
        {free ? null : (
          <p className="mt-1 text-right text-slate-500 text-xs tabular-nums">
            공급가 {won(view.price.supplyKrw)} + 부가세 {won(view.price.vatKrw)}
          </p>
        )}
        {recover && view.lastFailureMessage ? (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-red-800 text-xs">
            지난 결제를 하지 못했습니다 — {view.lastFailureMessage}
          </p>
        ) : null}
      </section>

      {/* 카드 */}
      <section className="grid gap-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <h2 className="font-semibold text-slate-900">결제 카드</h2>
        <CardFields value={card} onChange={setCard} inputClassName="h-12 text-base" />
        <p className="text-slate-500 text-xs">
          카드 정보는 결제대행사(페이업)로 바로 전달되고 저장하지 않습니다. 카드번호는 끝 4자리만 보관합니다.
        </p>
      </section>

      {/* 확인 사항 · 동의 */}
      <section className="grid gap-3 rounded-2xl bg-white p-5 text-sm shadow-sm ring-1 ring-slate-200">
        <h2 className="font-semibold text-slate-900">확인 사항</h2>
        <TermsSummary
          subject={{
            brandName: view.brandName,
            query: view.query,
            amountKrw: view.price.amountKrw,
            startOn: view.startOn,
          }}
        />
        <a href="/subscription-terms" target="_blank" rel="noopener" className="w-fit text-live text-xs underline">
          구독 서비스 이용약관 전문 보기
        </a>
        <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl bg-slate-50 px-3 py-2">
          <input
            type="checkbox"
            className="h-5 w-5 shrink-0 accent-live"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span className="font-medium text-slate-900">위 내용과 이용약관(정기결제 포함)에 동의합니다</span>
        </label>
      </section>

      <FieldError>{error}</FieldError>
      {left < view.attemptsLeft && left > 0 ? (
        <p className="text-slate-500 text-xs">카드 입력 기회가 {left}번 남았습니다.</p>
      ) : null}
      {left <= 0 ? (
        <p className="text-red-700 text-sm">카드 입력 횟수를 넘었습니다. 담당자에게 새 링크를 요청해 주세요.</p>
      ) : null}

      <SupportContact className="pt-2" />

      {/* 아래 고정 결제 버튼 — 엄지가 닿는 자리. 홈 표시줄만큼 띄운다 */}
      <div className="fixed inset-x-0 bottom-0 border-slate-200 border-t bg-white/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur">
        <div className="mx-auto max-w-md">
          <Button
            type="submit"
            form="pay-form"
            variant="live"
            className="h-12 w-full rounded-xl text-base"
            disabled={busy || !consent || left <= 0}
          >
            {cta}
          </Button>
          {!consent && !busy ? (
            <p className="mt-1.5 text-center text-slate-500 text-xs">확인 사항에 동의하면 결제할 수 있습니다</p>
          ) : null}
        </div>
      </div>
    </form>
  );
}

function Paid({ result }: { result: Done }) {
  return (
    <Result tone="green" title={result.paidKrw > 0 ? '결제가 완료됐습니다' : '구독을 시작했습니다'}>
      <dl className="mt-2 grid gap-2 rounded-xl bg-slate-50 p-4 text-left text-sm">
        <Row label="브랜드">{result.brandName}</Row>
        <Row label="구독">{result.subscriptionName}</Row>
        {result.paidKrw > 0 ? <Row label="결제 금액">{won(result.paidKrw)}</Row> : null}
        <Row label="결제 카드">
          {result.card.cardName} •••• {result.card.last4}
        </Row>
        {result.nextChargeOn ? <Row label="다음 결제일">{dot(result.nextChargeOn)}</Row> : null}
      </dl>
      <p className="mt-4">이 창은 닫으셔도 됩니다. 결제 내역은 {SHELL.serviceName}에 로그인하면 볼 수 있습니다.</p>
    </Result>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="break-keep text-right font-medium text-slate-900">{children}</dd>
    </div>
  );
}

const TONE = {
  green: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  red: 'bg-red-50 text-red-600',
} as const;

function Result({ tone, title, children }: { tone: keyof typeof TONE; title: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-6">
      <section className="rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-slate-200">
        <span className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${TONE[tone]}`}>
          {tone === 'green' ? <CheckIcon className="h-6 w-6" strokeWidth={3} /> : <AlertIcon className="h-6 w-6" />}
        </span>
        <h1 className="mt-4 font-bold text-xl">{title}</h1>
        <div className="mt-3 break-keep text-slate-600 text-sm leading-relaxed">{children}</div>
      </section>
      {tone === 'green' ? null : <SupportContact />}
    </div>
  );
}
